import { SWRConfiguration, mutate } from 'swr';

import useToast from '@/components/Toast/useToast';
import { FetcherParams } from '@/data/fetcher';
import {
  UseSWRMutationOptions,
  useOrganizationId,
  useSWR,
  useSWRMutation,
} from '@/hooks/useSwr';
import {
  CreateOAuthProviderBody,
  OAuthProviderSchema,
  UpdateOAuthProviderBody,
} from '@/types/schema';
import { hostApiPathV2 } from '@/utils/constants';

export const useOAuthProviders = (
  params?: { provider_id_contains?: string } | null,
  options?: SWRConfiguration
) => {
  const organizationId = useOrganizationId();
  return useSWR<OAuthProviderSchema[], { provider_id_contains?: string }>(
    params
      ? {
          url: `${hostApiPathV2}/auth/providers`,
          params,
          headers: { 'X-Tenant-Id': organizationId },
        }
      : null,
    options
  );
};

export const useCreateOAuthProviderMutation = (
  options?: UseSWRMutationOptions<OAuthProviderSchema, CreateOAuthProviderBody>
) => {
  const organizationId = useOrganizationId();
  const { createToast } = useToast();
  return useSWRMutation<OAuthProviderSchema, CreateOAuthProviderBody>(
    {
      method: 'POST',
      url: `${hostApiPathV2}/auth/providers`,
      headers: { 'X-Tenant-Id': organizationId },
    },
    {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        createToast({
          title: 'OAuth provider created successfully',
          type: 'success',
        });
        // Refresh the providers list
        mutate((key: FetcherParams) =>
          key?.url?.includes(`${hostApiPathV2}/auth/providers`)
        );
      },
      onError: (error, ...args) => {
        options?.onError?.(error, ...args);
        createToast({
          title: 'Failed to create OAuth provider',
          description: error.message,
          type: 'error',
        });
      },
    }
  );
};

export const useUpdateOAuthProviderMutation = (
  providerId: string,
  options?: UseSWRMutationOptions<OAuthProviderSchema, UpdateOAuthProviderBody>
) => {
  const organizationId = useOrganizationId();
  const { createToast } = useToast();
  return useSWRMutation<OAuthProviderSchema, UpdateOAuthProviderBody>(
    {
      method: 'PATCH',
      url: `${hostApiPathV2}/auth/providers/${providerId}`,
      headers: { 'X-Tenant-Id': organizationId },
    },
    {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        createToast({
          title: 'OAuth provider updated successfully',
          type: 'success',
        });
        // Refresh the providers list
        mutate((key: FetcherParams) =>
          key?.url?.includes(`${hostApiPathV2}/auth/providers`)
        );
      },
      onError: (error, ...args) => {
        options?.onError?.(error, ...args);
        createToast({
          title: 'Failed to update OAuth provider',
          description: error.message,
          type: 'error',
        });
      },
    }
  );
};

export const useDeleteOAuthProviderMutation = (providerId: string) => {
  const organizationId = useOrganizationId();
  const { createToast } = useToast();
  return useSWRMutation<void, never>(
    {
      method: 'DELETE',
      url: `${hostApiPathV2}/auth/providers/${providerId}`,
      headers: { 'X-Tenant-Id': organizationId },
    },
    {
      onSuccess: () => {
        createToast({
          title: 'OAuth provider deleted successfully',
          type: 'success',
        });
        // Refresh the providers list
        mutate((key: FetcherParams) =>
          key?.url?.includes(`${hostApiPathV2}/auth/providers`)
        );
      },
      onError: (error) => {
        createToast({
          title: 'Failed to delete OAuth provider',
          description: error.message,
          type: 'error',
        });
      },
    }
  );
};
